import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  fetchProgramNewsItems,
  type ProgramNewsItem,
  type ProgramNewsItemCounts,
} from "@/services/adminProgramNews";
import ArticleRow from "./ArticleRow";
import ArticleStatusTiles, { type ArticleFilter } from "./ArticleStatusTiles";
import UnreviewedToggle from "./UnreviewedToggle";
import TeamIdFilter from "./TeamIdFilter";
import PanelIntro from "./PanelIntro";
import { PANEL_INTROS } from "./panelIntros";

const PAGE_LIMIT = 50;
const DEBOUNCE_MS = 300;

const ArticlesPanel = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ArticleFilter>("all");
  const [teamInput, setTeamInput] = useState("");
  const teamId = useDebouncedValue(teamInput.trim(), DEBOUNCE_MS);
  const [unreviewedOnly, setUnreviewedOnly] = useState(false);
  const [counts, setCounts] = useState<Partial<ProgramNewsItemCounts>>({});
  const [items, setItems] = useState<ProgramNewsItem[]>([]);

  // `quiet` refreshes in place so an open detail sheet is not torn down.
  const load = useCallback(
    async (quiet = false) => {
      try {
        if (!quiet) setLoading(true);
        const response = await fetchProgramNewsItems({
          status: filter === "all" ? undefined : filter,
          teamId: teamId || undefined,
          reviewed: unreviewedOnly ? "no" : undefined,
          limit: PAGE_LIMIT,
        });
        setCounts(response.counts ?? {});
        setItems(Array.isArray(response.items) ? response.items : []);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        toast({ title: "Failed to load articles", description: message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    },
    [filter, teamId, unreviewedOnly, toast]
  );

  useEffect(() => {
    load();
  }, [load]);

  const refreshQuietly = useCallback(() => {
    load(true);
  }, [load]);

  return (
    <div className="space-y-4">
      <PanelIntro intro={PANEL_INTROS.articles} />
      <ArticleStatusTiles counts={counts} active={filter} onChange={setFilter} />
      <div className="flex flex-wrap items-center gap-3">
        <TeamIdFilter value={teamInput} onChange={setTeamInput} />
        <UnreviewedToggle
          id="program-news-articles-unreviewed"
          checked={unreviewedOnly}
          onChange={setUnreviewedOnly}
        />
      </div>
      {loading ? (
        <div className="py-8 text-center text-muted-foreground">Loading articles...</div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">No articles match this filter.</div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Published</TableHead>
                <TableHead>Team</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(item => (
                <ArticleRow key={item._id} item={item} onReviewChanged={refreshQuietly} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ArticlesPanel;
